import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Recommended learning path editor — per grade, cross-track. Two lists:
 *
 *   [ INCLUDED ] (ordered)          [ EXCLUDED ] (unordered)
 *   drag to reorder                 pool of not-yet-included lessons
 *
 * Drag between the lists to include / exclude. Drag within the included list
 * to reorder. Save writes recommended_order = (index+1)*10 for included, NULL
 * for excluded. Server also runs the §2.2 DAG-vs-order contradiction check
 * on save — offending lessons are highlighted here and the user can override
 * with "שמור בכל זאת" (writes with force=true).
 *
 * Multi-container DnD via @dnd-kit: two SortableContexts inside one
 * DndContext, with cross-container drops handled by inspecting the drop
 * target's container in onDragOver / onDragEnd.
 *
 * Extracted from the old standalone screen into a component so it drops
 * straight into the grade page as a tab. No page chrome inside — the tab is
 * the outer container.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Modal, Space, Spin, Tag, Typography } from 'antd';
import { HolderOutlined, SaveOutlined } from '@ant-design/icons';
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useDroppable, useSensor, useSensors, } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy, } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ApiError } from '../../api/client';
import { getRecommendedPath, saveRecommendedPath, } from '../../api';
import { notifySuccess } from '../errors';
const { Text } = Typography;
export function RecommendedPathEditor({ gradeId }) {
    const [loading, setLoading] = useState(true);
    const [included, setIncluded] = useState([]);
    const [excluded, setExcluded] = useState([]);
    const [saving, setSaving] = useState(false);
    const [contradictions, setContradictions] = useState([]);
    const [contradictionDialog, setContradictionDialog] = useState(null);
    const reload = useCallback(async () => {
        setLoading(true);
        setContradictions([]);
        try {
            const r = await getRecommendedPath(gradeId);
            setIncluded(r.included);
            setExcluded(r.excluded);
        }
        catch { /* client auto-surfaces */ }
        finally {
            setLoading(false);
        }
    }, [gradeId]);
    useEffect(() => { void reload(); }, [reload]);
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
    const includedIds = useMemo(() => included.map((l) => l.id), [included]);
    const excludedIds = useMemo(() => excluded.map((l) => l.id), [excluded]);
    const findContainer = useCallback((id) => {
        if (includedIds.includes(id))
            return 'included';
        if (excludedIds.includes(id))
            return 'excluded';
        return null;
    }, [includedIds, excludedIds]);
    const onDragOver = (e) => {
        if (!e.over)
            return;
        const activeId = Number(e.active.id);
        const overRaw = e.over.id;
        const from = findContainer(activeId);
        const to = typeof overRaw === 'string'
            ? (overRaw === 'excluded-empty' ? 'excluded' : 'included')
            : findContainer(Number(overRaw));
        if (!from || !to || from === to)
            return;
        const nextIncluded = [...included];
        const nextExcluded = [...excluded];
        const src = from === 'included' ? nextIncluded : nextExcluded;
        const dst = to === 'included' ? nextIncluded : nextExcluded;
        const srcIdx = src.findIndex((l) => l.id === activeId);
        if (srcIdx < 0)
            return;
        const [moved] = src.splice(srcIdx, 1);
        const overIdx = typeof overRaw === 'string'
            ? dst.length
            : dst.findIndex((l) => l.id === Number(overRaw));
        dst.splice(overIdx < 0 ? dst.length : overIdx, 0, moved);
        setIncluded(nextIncluded);
        setExcluded(nextExcluded);
    };
    const onDragEnd = (e) => {
        const activeId = Number(e.active.id);
        const overId = e.over ? Number(e.over.id) : null;
        if (overId === null || activeId === overId)
            return;
        if (typeof e.over.id === 'string')
            return;
        const from = findContainer(activeId);
        const to = findContainer(overId);
        if (from === 'included' && to === 'included') {
            setIncluded((prev) => arrayMove(prev, prev.findIndex((l) => l.id === activeId), prev.findIndex((l) => l.id === overId)));
        }
    };
    const save = useCallback(async (force) => {
        setSaving(true);
        try {
            await saveRecommendedPath(gradeId, included.map((l) => l.id), force);
            notifySuccess('המסלול המומלץ נשמר');
            setContradictions([]);
            setContradictionDialog(null);
            await reload();
        }
        catch (err) {
            if (err instanceof ApiError && err.code === 'recommended_order_contradicts_dag') {
                const list = err.details.contradictions ?? [];
                setContradictions(list);
                setContradictionDialog({ contradictions: list, ids: included.map((l) => l.id) });
                return;
            }
            // Any code other than recommended_order_contradicts_dag auto-surfaces
            // via the API client (saveRecommendedPath opts-out of just that one).
        }
        finally {
            setSaving(false);
        }
    }, [gradeId, included, reload]);
    if (loading)
        return _jsx(Spin, {});
    const titleById = new Map();
    included.concat(excluded).forEach((l) => titleById.set(l.id, l.title));
    return (_jsxs(_Fragment, { children: [_jsxs(Card, { title: _jsxs(Space, { children: [_jsx("span", { children: "\u05DE\u05E1\u05DC\u05D5\u05DC \u05D4\u05DC\u05D9\u05DE\u05D5\u05D3 \u05D4\u05DE\u05D5\u05DE\u05DC\u05E5" }), _jsxs(Text, { type: "secondary", children: ["(", included.length, " \u05DB\u05DC\u05D5\u05DC\u05D9\u05DD \u00B7 ", excluded.length, " \u05DE\u05D7\u05D5\u05E5 \u05DC\u05DE\u05E1\u05DC\u05D5\u05DC)"] })] }), extra: _jsx(Button, { type: "primary", icon: _jsx(SaveOutlined, {}), loading: saving, onClick: () => save(false), children: "\u05E9\u05DE\u05D5\u05E8" }), children: [contradictions.length > 0 && (_jsx(Alert, { type: "warning", showIcon: true, style: { marginBottom: 12 }, message: `נמצאו ${contradictions.length} סתירות מול גרף התנאי-קדם`, description: "\u05E9\u05D9\u05E2\u05D5\u05E8\u05D9\u05DD \u05D1\u05E6\u05D4\u05D5\u05D1 \u05DE\u05D5\u05E4\u05D9\u05E2\u05D9\u05DD \u05DC\u05E4\u05E0\u05D9 \u05E9\u05D9\u05E2\u05D5\u05E8 \u05E9\u05D4\u05DD \u05EA\u05DC\u05D5\u05D9\u05D9\u05DD \u05D1\u05D5. \u05D0\u05E4\u05E9\u05E8 \u05DC\u05EA\u05E7\u05DF \u05D0\u05EA \u05D4\u05E1\u05D3\u05E8 \u05D0\u05D5 \u05DC\u05E9\u05DE\u05D5\u05E8 \u05D1\u05DB\u05DC \u05D6\u05D0\u05EA." })), _jsx(DndContext, { sensors: sensors, collisionDetection: closestCenter, modifiers: [restrictToVerticalAxis], onDragOver: onDragOver, onDragEnd: onDragEnd, children: _jsxs("div", { style: { display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }, children: [_jsx(ListColumn, { container: "included", title: "\u05D1\u05DE\u05E1\u05DC\u05D5\u05DC \u05D4\u05DE\u05D5\u05DE\u05DC\u05E5 (\u05E1\u05D3\u05E8 \u05DC\u05D9\u05DE\u05D5\u05D3)", lessons: included, contradictionIds: new Set(contradictions.flatMap((c) => [c.lesson_id, c.conflicting_dep_id])), emptyDroppableId: "included-empty", emptyHint: "\u05D2\u05E8\u05D5\u05E8 \u05DC\u05DB\u05D0\u05DF \u05E9\u05D9\u05E2\u05D5\u05E8\u05D9\u05DD \u05DE\u05D4\u05E6\u05D3", showIndex: true }), _jsx(ListColumn, { container: "excluded", title: "\u05DE\u05D7\u05D5\u05E5 \u05DC\u05DE\u05E1\u05DC\u05D5\u05DC", lessons: excluded, contradictionIds: new Set(), emptyDroppableId: "excluded-empty", emptyHint: "\u05D2\u05E8\u05D5\u05E8 \u05DC\u05DB\u05D0\u05DF \u05DB\u05D3\u05D9 \u05DC\u05D4\u05D5\u05E6\u05D9\u05D0 \u05DE\u05D4\u05DE\u05E1\u05DC\u05D5\u05DC", showIndex: false })] }) })] }), _jsxs(Modal, { open: contradictionDialog !== null, title: "\u05E1\u05EA\u05D9\u05E8\u05D4 \u05DE\u05D5\u05DC \u05D2\u05E8\u05E3 \u05D4\u05EA\u05E0\u05D0\u05D9-\u05E7\u05D3\u05DD", okText: "\u05E9\u05DE\u05D5\u05E8 \u05D1\u05DB\u05DC \u05D6\u05D0\u05EA", cancelText: "\u05D7\u05D6\u05D5\u05E8 \u05DC\u05E2\u05E8\u05D9\u05DB\u05D4", okButtonProps: { danger: true }, confirmLoading: saving, onOk: () => save(true), onCancel: () => setContradictionDialog(null), children: [_jsx("p", { children: "\u05E9\u05D9\u05E2\u05D5\u05E8\u05D9\u05DD \u05D1\u05DE\u05E1\u05DC\u05D5\u05DC \u05D4\u05DE\u05D5\u05DE\u05DC\u05E5 \u05E9\u05E1\u05D5\u05EA\u05E8\u05D9\u05DD \u05D0\u05EA \u05D2\u05E8\u05E3 \u05D4\u05EA\u05E0\u05D0\u05D9-\u05E7\u05D3\u05DD (\u05DE\u05D5\u05E4\u05D9\u05E2\u05D9\u05DD \u05DC\u05E4\u05E0\u05D9 \u05E9\u05D9\u05E2\u05D5\u05E8 \u05E9\u05D4\u05DD \u05EA\u05DC\u05D5\u05D9\u05D9\u05DD \u05D1\u05D5):" }), contradictionDialog && (_jsx("ul", { children: contradictionDialog.contradictions.map((c, i) => (_jsxs("li", { children: [_jsx("b", { children: titleById.get(c.lesson_id) ?? `שיעור #${c.lesson_id}` }), ' — תלוי ב־', _jsx("b", { children: titleById.get(c.conflicting_dep_id) ?? `שיעור #${c.conflicting_dep_id}` }), ' שמופיע אחריו במסלול.'] }, i))) })), _jsx("p", { style: { marginTop: 12 }, children: "\u05DC\u05E9\u05DE\u05D5\u05E8 \u05D1\u05DB\u05DC \u05D6\u05D0\u05EA?" })] })] }));
}
function ListColumn({ title, lessons, contradictionIds, emptyDroppableId, emptyHint, showIndex, }) {
    const { setNodeRef, isOver } = useDroppable({ id: emptyDroppableId });
    return (_jsx(Card, { size: "small", title: title, children: _jsx(SortableContext, { items: lessons.map((l) => l.id), strategy: verticalListSortingStrategy, children: _jsx("div", { ref: setNodeRef, style: {
                    minHeight: 80, padding: 4,
                    background: isOver && lessons.length === 0 ? '#e6f4ff' : undefined,
                    borderRadius: 4,
                }, children: lessons.length === 0
                    ? _jsx("div", { style: { color: '#9ca3af', textAlign: 'center', padding: 24 }, children: emptyHint })
                    : lessons.map((l, i) => (_jsx(LessonRow, { lesson: l, index: showIndex ? i + 1 : null, highlight: contradictionIds.has(l.id) }, l.id))) }) }) }));
}
function LessonRow({ lesson, index, highlight }) {
    const { setNodeRef, transform, transition, isDragging, attributes, listeners } = useSortable({ id: lesson.id });
    const style = {
        transform: CSS.Translate.toString(transform),
        transition,
        background: highlight ? '#fff7e6' : isDragging ? '#e6f4ff' : '#fff',
        border: highlight ? '1px solid #ffa940' : '1px solid #f0f0f0',
        borderRadius: 4,
        padding: '8px 10px',
        marginBottom: 6,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
    };
    return (_jsxs("div", { ref: setNodeRef, style: style, children: [_jsx("button", { type: "button", "aria-label": "\u05D2\u05E8\u05D5\u05E8 \u05DC\u05E1\u05D9\u05D3\u05D5\u05E8", ...attributes, ...listeners, style: {
                    cursor: 'grab', touchAction: 'none', border: 0, background: 'transparent',
                    color: '#8c8c8c', padding: 4, display: 'inline-flex', alignItems: 'center',
                }, children: _jsx(HolderOutlined, {}) }), index !== null && (_jsxs("div", { style: { minWidth: 28, fontWeight: 600, color: '#1e40af' }, children: [index, "."] })), _jsx("div", { style: { flex: 1, minWidth: 0 }, children: _jsx("div", { style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }, children: lesson.title }) }), _jsx(Tag, { children: lesson.track_name }), lesson.is_optional && _jsx(Tag, { color: "default", children: "\u05E8\u05E9\u05D5\u05EA" })] }));
}
